import React from "react";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import {
  GetIntegrationHistoryParams,
  IntegrationsHistoryItem,
} from "features/integrations/integrations";
import { formatIntegrationDateTime } from "utils/dateUtils";
import CustomScrollbars from "../CustomScrollbars";

interface IntegrationDetailsTableProps {
  history: IntegrationsHistoryItem[];
  isFetching: boolean;
  sortBy: GetIntegrationHistoryParams["sort_by"];
  sortOrder: "asc" | "desc";
  onSortChange: (field: GetIntegrationHistoryParams["sort_by"]) => void;
  timezone?: string;
  pagination?: React.ReactNode;
}

const headerCellStyles = {
  fontWeight: "bold",
  color: "white",
  fontSize: "1rem",
  borderTop: "none",
  borderLeft: "1px solid",
  borderRight: "1px solid",
  borderBottom: "1px solid",
  borderColor: "primary.main",
  backgroundColor: "primary.main",
  textAlign: "center",
};

const bodyCellStyles = {
  fontWeight: 300,
  fontSize: "0.875rem",
  borderRight: "1px solid red",
  borderColor: "primary.main",
  textAlign: "center",
  padding: 0.2,
  "&:last-child": { borderRight: "none" },
};

export default function IntegrationDetailsTable({
  history,
  isFetching,
  sortBy,
  sortOrder,
  onSortChange,
  timezone,
  pagination,
}: IntegrationDetailsTableProps) {
  if (history.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "calc(100vh - 350px)",
        }}
      >
        <Typography p={2} variant="h6" align="center" color="textSecondary">
          No history found
        </Typography>
      </Box>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        borderRadius: 2,
        boxShadow: 1,
        border: "1px solid",
        borderColor: "divider",
        overflow: "clip",
      }}
    >
      <CustomScrollbars sx={{ flex: 1, minWidth: 0, overflowX: "auto" }} trackOffsetTop={40}>
      <Table stickyHeader size="small" sx={{ minWidth: 320 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={headerCellStyles}>
              <TableSortLabel
                active={sortBy === "date"}
                direction={sortBy === "date" ? sortOrder : "asc"}
                onClick={() => onSortChange("date")}
                sx={{
                  color: "white !important",
                  "&:hover": { color: "white" },
                  "& .MuiTableSortLabel-icon": { color: "white !important" },
                  marginLeft: "18px",
                }}
              >
                Date
              </TableSortLabel>
            </TableCell>
            <TableCell sx={headerCellStyles}>
              <TableSortLabel
                active={sortBy === "completed"}
                direction={sortBy === "completed" ? sortOrder : "asc"}
                onClick={() => onSortChange("completed")}
                sx={{
                  color: "white !important",
                  "&:hover": { color: "white" },
                  "& .MuiTableSortLabel-icon": { color: "white !important" },
                  marginLeft: "18px",
                }}
              >
                Completed
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {history.map((detail, index) => (
            <TableRow
              key={index}
              sx={{
                "&:last-child td, &:last-child th": {
                  borderBottom: "none",
                },
              }}
            >
              <TableCell sx={bodyCellStyles}>
                {formatIntegrationDateTime(
                  detail.date,
                  timezone,
                )}
              </TableCell>
              <TableCell sx={bodyCellStyles}>
                {detail.completed ? (
                  <CheckIcon sx={{ color: "secondary.main" }} />
                ) : (
                  <CloseIcon sx={{ color: "red" }} />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </CustomScrollbars>
      {pagination}
    </Paper>
  );
}
